import React from "react";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { InterestCta, TextLink } from "../components/Bits";
import { ROUTES, breadcrumbs, organization } from "../lib/seo";
import { ABOUT_PHOTO, DIYA_PHOTO, KASHI_SITE } from "../lib/constants";

const KASHI = {
  immersion: `${KASHI_SITE}/the-immersion/`,
  editions: `${KASHI_SITE}/editions/`,
  voices: `${KASHI_SITE}/voices/`,
  brochure: `${KASHI_SITE}/brochure/`,
};

export default function KashiWithAnshu() {
  return (
    <>
      <Seo route={ROUTES.kashi} type="article" />
      <JsonLd data={[organization(), breadcrumbs([{ name: ROUTES.kashi.crumb, path: ROUTES.kashi.path }])]} />

      <PageHero
        eyebrow="The parent brand"
        title={<>Kashi with <span className="gold-grad italic">Anshu</span></>}
        intro="Antara didn't start in an auditorium. It started on the water in Banaras, at dawn, with a small group of people singing. This is the work it grew out of."
      >
        <p className="mt-4 font-deva text-2xl" style={{ color: "#C9A961" }}>काशी</p>
      </PageHero>

      {/* Who / what — light section */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <Eyebrow>The invitation</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">A city, and a way of being in it</h2>
            <p className="mt-6 font-body text-lg text-ink-2/90 leading-relaxed">
              Kashi with Anshu is a small, deliberate thing. Four days in Banaras — Kashi, the oldest
              living city — for ten seekers at a time, by application rather than booking. Anshu Goyal
              leads it the way you'd take a close friend through a place you love: the rare aartis, the
              lanes most visitors never find, the quiet of the ghats before the crowds, a heritage
              stay to come back to.
            </p>
            <p className="mt-5 font-body text-lg text-ink-2/90 leading-relaxed">
              The line Anshu keeps returning to says it best: <span className="font-serif-italic text-amber">
              "I don't take you to Kashi. I take you closer to yourself."</span> It isn't a tour. You can
              read how it's built on the <TextLink href={KASHI.immersion}>immersion page</TextLink>, and
              what past travellers say in their own words in <TextLink href={KASHI.voices}>their voices</TextLink>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Bhajan on the Ganga — dark */}
      <section className="bg-[#0F0A07] py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Where the singing began</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight" style={{ color: "#FBF6EE" }}>
              Bhajan jamming,<br />on a boat, on the river
            </h2>
            <p className="mt-6 font-body text-base md:text-lg leading-relaxed" style={{ color: "#E8D9C5" }}>
              On the <TextLink href="https://kashiwithanshu.com/the-immersion/"><span style={{ color: "#E5C880" }}>immersion</span></TextLink>,
              one part of it always turns into music. A boat pushes off into the Ganga by lamplight,
              drifts past Manikarnika, and somewhere along the way someone starts a bhajan. By the
              second verse nobody is a listener any more. That moment — the whole group singing over
              moving water, no sound system, no stage, no audience — is the exact seed Antara grew from.
            </p>
            <p className="mt-5 font-body text-base md:text-lg leading-relaxed" style={{ color: "#E8D9C5" }}>
              Antara is that same idea, carried off the river and into a room so more people can be in
              it — the boat, made large enough for a city to sit in. If you want the plain version of
              what happens at an Antara evening, here's{" "}
              <TextLink to={ROUTES.pillar.path}><span style={{ color: "#E5C880" }}>what bhajan jamming is</span></TextLink>.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-[#C9A961]/25 to-transparent blur-xl" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] border" style={{ borderColor: "rgba(201,169,97,0.3)" }}>
                <img src={DIYA_PHOTO} alt="A diya on the Ganga at Kashi — where Antara's bhajan jamming began" width="640" height="1136" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How Antara grew out of it — light */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] border border-gold/30 bg-gradient-to-br from-[#FBF6EE] to-[#E8D9C5]">
              <img src={ABOUT_PHOTO} alt="Anshu Goyal — Kashi with Anshu" width="1280" height="800" loading="lazy" decoding="async" className="w-full h-full object-contain" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>From Kashi to Antara</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">The same devotion, a bigger room</h2>
            <p className="mt-6 font-body text-lg text-ink-2/90 leading-relaxed">
              The immersions are, by design, tiny — ten people at a time. Antara is how that spirit
              reaches a few hundred: the same reverence, the same insistence that you sing rather than
              watch, staged with a live band and a real sound system. One is intimate and by
              application; the other is open to anyone who takes a seat.
            </p>
            <p className="mt-5 font-body text-lg text-ink-2/90 leading-relaxed">
              You can see Kashi's own calendar of gatherings on the{" "}
              <TextLink href={KASHI.editions}>Kashi editions page</TextLink>, or read the full story of
              the immersion in the <TextLink href={KASHI.brochure}>brochure</TextLink>. And when you're
              ready for the Delhi evening, the next Antara is{" "}
              <TextLink to={ROUTES.delhi.path}>here</TextLink>.
            </p>
            <LotusDivider />
            <InterestCta />
          </Reveal>
        </div>
      </section>
    </>
  );
}
